import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Key, Plus, Trash2, Copy, Loader2, ShieldAlert } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  listApiKeys,
  createApiKey,
  setApiKeyActive,
  deleteApiKey,
  getApiKeyUsage,
  apiKeyScopes,
} from "@/lib/api-keys.functions";

export const Route = createFileRoute("/_authenticated/app/settings/api-keys")({
  head: () => ({
    meta: [
      { title: "API keys — CairnOS" },
      { name: "description", content: "Issue, rotate, and revoke programmatic access keys." },
    ],
  }),
  component: ApiKeysAdmin,
});

function ApiKeysAdmin() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const listFn = useServerFn(listApiKeys);
  const createFn = useServerFn(createApiKey);
  const setActiveFn = useServerFn(setApiKeyActive);
  const deleteFn = useServerFn(deleteApiKey);

  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<{ key: string; prefix: string } | null>(null);

  const keysQ = useQuery({
    queryKey: ["api-keys", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const createMut = useMutation({
    mutationFn: (payload: { name: string; scopes: string[]; expiresAt: string | null; rateLimitPerMin: number; rateBurst: number }) =>
      createFn({
        data: {
          tenantId: tenantId!,
          name: payload.name,
          scopes: payload.scopes as any,
          expiresAt: payload.expiresAt,
          rateLimitPerMin: payload.rateLimitPerMin,
          rateBurst: payload.rateBurst,
        },
      }),
    onSuccess: (res) => {
      setRevealed({ key: res.fullKey, prefix: res.keyPrefix });
      setCreateOpen(false);
      void qc.invalidateQueries({ queryKey: ["api-keys", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      setActiveFn({ data: { id, active } }),
    onSuccess: () => {
      toast.success("Key updated");
      void qc.invalidateQueries({ queryKey: ["api-keys", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Key revoked");
      void qc.invalidateQueries({ queryKey: ["api-keys", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const keys = keysQ.data?.rows ?? [];

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Key className="h-6 w-6" /> API keys
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Programmatic access to <code className="font-mono">/api/public/v1/*</code>. Keys are
            shown once at creation — store them securely.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" /> New key
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active Keys</CardTitle>
          <CardDescription>
            Each request is hashed-and-checked, scope-filtered, and rate-limited. Usage stats
            update in near-real-time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {keysQ.isLoading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : keys.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No keys yet. Issue one to start integrating.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Usage (24h)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((k: any) => (
                  <KeyRow
                    key={k.id}
                    k={k}
                    onToggle={(active) => toggleMut.mutate({ id: k.id, active })}
                    onDelete={() => {
                      if (confirm(`Revoke "${k.name}"? Existing integrations using this key will stop working.`)) {
                        deleteMut.mutate(k.id);
                      }
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(p) => createMut.mutate(p)}
        submitting={createMut.isPending}
      />

      <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" /> Save this key now
            </DialogTitle>
            <DialogDescription>
              This is the only time the full key will be displayed. Store it in your secret
              manager — we keep only a hash.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">Prefix: <span className="font-mono">{revealed?.prefix}</span></div>
            <div className="flex items-center gap-2 bg-muted p-3 rounded font-mono text-xs break-all">
              {revealed?.key}
              <Button
                size="icon"
                variant="ghost"
                onClick={() => {
                  navigator.clipboard.writeText(revealed?.key ?? "");
                  toast.success("Copied");
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Send as <code>Authorization: Bearer &lt;key&gt;</code> or <code>X-Api-Key: &lt;key&gt;</code>.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealed(null)}>I've saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KeyRow({
  k, onToggle, onDelete,
}: { k: any; onToggle: (active: boolean) => void; onDelete: () => void }) {
  const usageFn = useServerFn(getApiKeyUsage);
  const usageQ = useQuery({
    queryKey: ["api-key-usage", k.id],
    queryFn: () => usageFn({ data: { id: k.id, hours: 24 } }),
    refetchInterval: 30_000,
  });

  const expired = k.expires_at && new Date(k.expires_at) < new Date();
  const u = usageQ.data;

  return (
    <TableRow>
      <TableCell className="font-medium">{k.name}</TableCell>
      <TableCell className="font-mono text-xs">{k.key_prefix}…</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1 max-w-[260px]">
          {(k.scopes ?? []).slice(0, 3).map((s: string) => (
            <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>
          ))}
          {(k.scopes ?? []).length > 3 && (
            <Badge variant="outline" className="text-[10px]">+{k.scopes.length - 3}</Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {k.rate_limit_per_min}/min · burst {k.rate_burst}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {k.last_used_at ? formatDistanceToNow(new Date(k.last_used_at), { addSuffix: true }) : "never"}
      </TableCell>
      <TableCell className="text-xs">
        {usageQ.isLoading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <div>
            <div>{u?.requestCount ?? 0} req · {u?.errorCount ?? 0} err</div>
            <div className="text-muted-foreground">p95 {u?.p95DurationMs ?? 0}ms</div>
          </div>
        )}
      </TableCell>
      <TableCell>
        {expired ? (
          <Badge variant="outline" className="bg-muted">expired</Badge>
        ) : k.is_active ? (
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">active</Badge>
        ) : (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 dark:text-amber-400">disabled</Badge>
        )}
        {k.expires_at && !expired && (
          <div className="text-[10px] text-muted-foreground mt-0.5">
            expires {formatDistanceToNow(new Date(k.expires_at), { addSuffix: true })}
          </div>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="inline-flex items-center gap-2">
          <Switch
            checked={k.is_active}
            onCheckedChange={(c) => onToggle(c)}
            disabled={expired}
          />
          <Button size="sm" variant="ghost" onClick={onDelete} title="Revoke">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function CreateKeyDialog({
  open, onClose, onSubmit, submitting,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (p: { name: string; scopes: string[]; expiresAt: string | null; rateLimitPerMin: number; rateBurst: number }) => void;
  submitting: boolean;
}) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresDays, setExpiresDays] = useState<string>("90");
  const [rateLimit, setRateLimit] = useState<number>(60);
  const [rateBurst, setRateBurst] = useState<number>(120);

  const toggle = (s: string) =>
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = () => {
    if (!name.trim() || scopes.length === 0) {
      toast.error("Name and at least one scope are required");
      return;
    }
    const days = parseInt(expiresDays, 10);
    const expiresAt =
      !expiresDays || days === 0 || Number.isNaN(days)
        ? null
        : new Date(Date.now() + days * 86400_000).toISOString();
    onSubmit({ name: name.trim(), scopes, expiresAt, rateLimitPerMin: rateLimit, rateBurst });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New API key</DialogTitle>
          <DialogDescription>
            Scopes follow the principle of least privilege — only grant what the integration needs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="ak-name">Name</Label>
            <Input id="ak-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Shopify connector" />
          </div>
          <div>
            <Label>Scopes</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {apiKeyScopes.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggle(s)}
                  className={`text-xs px-2 py-1 rounded border transition font-mono ${
                    scopes.includes(s)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background hover:bg-muted"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="ak-exp">Expires (days)</Label>
              <Input id="ak-exp" type="number" min="0" value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} />
              <p className="text-[10px] text-muted-foreground mt-1">0 = never</p>
            </div>
            <div>
              <Label htmlFor="ak-rate">Rate/min</Label>
              <Input id="ak-rate" type="number" min="1" value={rateLimit} onChange={(e) => setRateLimit(Number(e.target.value))} />
            </div>
            <div>
              <Label htmlFor="ak-burst">Burst</Label>
              <Input id="ak-burst" type="number" min="1" value={rateBurst} onChange={(e) => setRateBurst(Number(e.target.value))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}